// Default post-login landing = the approved Final Hub Center (HUB_ROOT in
// commandCenterHubModel.ts). Kept as a literal so this early-loaded auth module
// carries no dependency on the heavy command-center graph. Legacy /dashboard
// and the root landing redirect here too (see router.tsx).
export const DEFAULT_AUTH_NEXT = '/square/command-center'

/**
 * Sanitize a post-login `next` destination. Only an INTERNAL, absolute,
 * single-slash path is allowed; everything else falls back to
 * DEFAULT_AUTH_NEXT. Blocks open-redirect / dead-route vectors:
 *   - off-origin ("http://…", protocol-relative "//host")
 *   - backslash tricks ("/\\host", "\\host") some browsers normalize to "//"
 *   - control characters
 *   - a once-decoded protocol-relative / backslash target ("/%2f%2fhost",
 *     "/%5chost") — reject after a single decodeURIComponent pass.
 */
export function sanitizeAuthNext(next: string | null | undefined): string {
  if (!next) return DEFAULT_AUTH_NEXT
  if (!next.startsWith('/')) return DEFAULT_AUTH_NEXT
  if (next.startsWith('//')) return DEFAULT_AUTH_NEXT
  if (next.includes('\\')) return DEFAULT_AUTH_NEXT
  if (/[\x00-\x1f\x7f]/.test(next)) return DEFAULT_AUTH_NEXT
  let decoded: string
  try {
    decoded = decodeURIComponent(next)
  } catch {
    return DEFAULT_AUTH_NEXT
  }
  if (
    decoded.startsWith('//') ||
    decoded.includes('\\') ||
    /[\x00-\x1f\x7f]/.test(decoded)
  ) {
    return DEFAULT_AUTH_NEXT
  }
  return next
}

export function buildLoginHref(next: string | null | undefined): string {
  const safeNext = sanitizeAuthNext(next)
  return `/auth/login?next=${encodeURIComponent(safeNext)}`
}

// ------------------------------------------------------------------
// OAuth-round-trip persistence.
//
// The GitHub OAuth flow leaves the SPA entirely and returns via the
// provider → Supabase → app. Along that path the `?next=` query param
// can be dropped (e.g. a provider/Site-URL fallback), which would land
// the operator on DEFAULT_AUTH_NEXT (the Final Hub) instead of the
// route they asked for — e.g. a deep Hub link.
//
// To make the destination survive the round-trip, we stash it in
// sessionStorage before initiating login and recover it on return
// (AuthCallback, or the root landing if the provider fell back to the
// Site URL). This never widens what is allowed — every value still
// passes sanitizeAuthNext (must be an internal, single-slash path).
// ------------------------------------------------------------------

const AUTH_NEXT_STORAGE_KEY = 'sq.auth.next'

/** Persist the post-login destination before starting an OAuth round-trip. */
export function stashAuthNext(next: string | null | undefined): void {
  const safe = sanitizeAuthNext(next)
  try {
    if (safe && safe !== DEFAULT_AUTH_NEXT) {
      window.sessionStorage.setItem(AUTH_NEXT_STORAGE_KEY, safe)
    } else {
      window.sessionStorage.removeItem(AUTH_NEXT_STORAGE_KEY)
    }
  } catch {
    /* sessionStorage unavailable (SSR / privacy mode) — non-fatal */
  }
}

/** Read the stashed destination without consuming it (safe to call during render). */
export function peekStashedAuthNext(): string | null {
  try {
    return window.sessionStorage.getItem(AUTH_NEXT_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Remove the stashed destination once it has been honored. */
export function clearStashedAuthNext(): void {
  try {
    window.sessionStorage.removeItem(AUTH_NEXT_STORAGE_KEY)
  } catch {
    /* non-fatal */
  }
}
